import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Popover,
  MenuItem,
  Checkbox,
  Collapse,
  useTheme,
  CircularProgress,
} from '@mui/material';
import { Public as PublicIcon } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { updateCountryPreference, updateJurisdictionPreference, fetchJurisdictionTree } from '../../redux/actions/userAction';
import ReactGA from 'react-ga4';
import { tracker } from '../../analytics';
import FlagEmoji from './FlagEmoji';

const ALL_COUNTRIES = [
  { value: 'MA', flag: '🇲🇦', labelKey: 'country_maroc' },
  { value: 'AE', flag: '🇦🇪', labelKey: 'country_uae' },
];

const LEVEL_LABEL_KEYS = {
  federal: 'jurisdiction_federal',
  emirate: 'jurisdiction_emirates',
  free_zone: 'jurisdiction_free_zones',
};

// ─── Jurisdiction item row ──────────────────────────────────────────────────

const JurisdictionItem = ({ name, count, checked, onChange, disabled, indent = 0, theme }) => (
  <Box
    onClick={disabled ? undefined : onChange}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      py: 0.35,
      px: 1,
      pl: 1 + indent * 1.5,
      cursor: disabled ? 'default' : 'pointer',
      borderRadius: '4px',
      opacity: disabled ? 0.4 : 1,
      transition: 'background-color 0.12s',
      '&:hover': disabled ? {} : { backgroundColor: theme.palette.action.hover },
    }}
  >
    <Checkbox
      checked={checked}
      size="small"
      disabled={disabled}
      sx={{ p: 0.25, '& .MuiSvgIcon-root': { fontSize: '0.95rem' } }}
      disableRipple
    />
    <Typography
      sx={{
        fontSize: '0.74rem',
        fontWeight: checked ? 600 : 400,
        color: theme.palette.text.primary,
        flexGrow: 1,
      }}
    >
      {name}
    </Typography>
    {count > 0 && (
      <Typography sx={{ fontSize: '0.62rem', color: theme.palette.text.disabled, fontWeight: 500 }}>
        {count.toLocaleString()}
      </Typography>
    )}
  </Box>
);

// ─── Level group header ─────────────────────────────────────────────────────

const LevelHeader = ({ label, expanded, onToggle, theme }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.4,
      py: 0.3,
      px: 1,
      cursor: 'pointer',
      borderRadius: '4px',
      '&:hover': { backgroundColor: theme.palette.action.hover },
    }}
  >
    {expanded
      ? <ExpandMoreIcon sx={{ fontSize: '0.85rem', color: theme.palette.text.disabled }} />
      : <ChevronRightIcon sx={{ fontSize: '0.85rem', color: theme.palette.text.disabled }} />
    }
    <Typography sx={{
      fontSize: '0.68rem',
      fontWeight: 700,
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
    }}>
      {label}
    </Typography>
  </Box>
);

// ─── Main component ─────────────────────────────────────────────────────────

const CompactCountryLanguageSelector = ({
  disabled = false,
  onSelect = null,
  displayMode = 'dropdown',
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.language || 'en').split('-')[0];

  const countryPreference = useSelector(state => state.user?.countryPreference);
  const jurisdictionPreference = useSelector(state => state.user?.jurisdictionPreference) || {};
  const jurisdictionTrees = useSelector(state => state.user?.jurisdictionTrees) || {};
  const [isSaving, setIsSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const iconButtonRef = useRef(null);
  const [expandedLevels, setExpandedLevels] = useState({});
  const [expandedFzParents, setExpandedFzParents] = useState({});

  const selectedCountries = Array.isArray(countryPreference)
    ? countryPreference
    : countryPreference ? [countryPreference] : ['MA'];

  // Fetch jurisdiction trees for selected countries when popover opens
  useEffect(() => {
    if (anchorEl) {
      selectedCountries.forEach(c => {
        if (!jurisdictionTrees[c]) dispatch(fetchJurisdictionTree(c));
      });
    }
  }, [anchorEl, selectedCountries, jurisdictionTrees, dispatch]);

  // ─── Country toggle ───────────────────────────────────────────────────

  const handleToggleCountry = async (countryValue) => {
    let newCountries;
    if (selectedCountries.includes(countryValue)) {
      if (selectedCountries.length === 1) return;
      newCountries = selectedCountries.filter(c => c !== countryValue);
    } else {
      newCountries = [...selectedCountries, countryValue];
      if (!jurisdictionTrees[countryValue]) dispatch(fetchJurisdictionTree(countryValue));
    }

    setIsSaving(true);
    try {
      await dispatch(updateCountryPreference(newCountries));
      ReactGA.event({ category: 'Country', action: 'Country Selection Changed', label: `Countries: ${newCountries.join(',')}` });
      tracker.event({ category: 'Country', action: 'Country Selection Changed', label: `Countries: ${newCountries.join(',')}` });
      if (onSelect) onSelect(newCountries);
    } catch (error) {
      console.error('Failed to update country preference:', error);
    }
    setIsSaving(false);
  };

  // ─── Jurisdiction logic ───────────────────────────────────────────────
  //
  // State semantics:
  //   jurisdictionPreference[country] === undefined/null  →  "All" (no filter)
  //   jurisdictionPreference[country] === ['AE_DUBAI']    →  only Dubai selected
  //
  // UX: clicking a jurisdiction when in "All" mode switches to selection
  // mode with ONLY that jurisdiction. This lets the user go from "All"
  // to "Sharjah only" in a single click.

  const getName = useCallback((nameObj) => {
    if (!nameObj) return '';
    if (typeof nameObj === 'string') return nameObj;
    return nameObj[currentLang] || nameObj.en || nameObj.ar || nameObj.fr || Object.values(nameObj)[0] || '';
  }, [currentLang]);

  const isAll = (cc) => {
    const pref = jurisdictionPreference[cc];
    return pref === undefined || pref === null;
  };

  const isChecked = (cc, code) => {
    if (isAll(cc)) return false; // In "All" mode nothing is individually checked
    return (jurisdictionPreference[cc] || []).includes(code);
  };

  const handleClickAll = (cc) => {
    // Always resets to "All" (no filter)
    dispatch(updateJurisdictionPreference(cc, null));
  };

  const handleClickJurisdiction = (cc, code, allCodes) => {
    if (isAll(cc)) {
      // Switch from "All" to selecting ONLY this one jurisdiction
      dispatch(updateJurisdictionPreference(cc, [code]));
      return;
    }

    const current = jurisdictionPreference[cc] || [];
    if (current.includes(code)) {
      // Unchecking — if it's the last one, snap back to "All"
      const next = current.filter(c => c !== code);
      dispatch(updateJurisdictionPreference(cc, next.length === 0 ? null : next));
    } else {
      // Checking — if selecting all, snap to "All"
      const next = [...current, code];
      dispatch(updateJurisdictionPreference(cc, next.length >= allCodes.length ? null : next));
    }
  };

  // ─── Display labels ───────────────────────────────────────────────────

  // Resolve a jurisdiction code to its short display name from the tree data
  const getJurisdictionShortName = useCallback((cc, code) => {
    const treeData = jurisdictionTrees[cc];
    if (!treeData) return code;
    for (const group of treeData.tree) {
      if (group.level === 'free_zone') {
        for (const pg of group.children) {
          for (const z of pg.zones) {
            if (z.code === code) return getName(z.name);
          }
        }
      } else {
        for (const child of group.children) {
          if (child.code === code) return getName(child.name);
        }
      }
    }
    return code;
  }, [jurisdictionTrees, getName]);

  // Build the jurisdiction part of the chip label for a single country.
  // "All" → "All", specific → "Dubai, DIFC" (≤2) or "Dubai +2" (>2)
  // Returns null if country has no sub-jurisdictions.
  const getJurisdictionLabel = (cc) => {
    const tree = jurisdictionTrees[cc];
    if (!tree?.has_sub_jurisdictions) return null;

    const pref = jurisdictionPreference[cc];
    if (!pref || !Array.isArray(pref) || pref.length === 0) {
      return t('jurisdiction_select_all');
    }
    const names = pref.map(code => getJurisdictionShortName(cc, code));
    if (names.length <= 2) return names.join(', ');
    return `${names[0]} +${names.length - 1}`;
  };

  const getDisplayLabel = () => {
    if (selectedCountries.length === 0) return '🌍';
    const first = ALL_COUNTRIES.find(c => c.value === selectedCountries[0]);
    if (!first) return '🌍';
    const jurLabel = getJurisdictionLabel(first.value);
    const countryName = t(first.labelKey, first.value);
    const sep = jurLabel ? ` · ${jurLabel}` : '';

    if (selectedCountries.length === 1) {
      return <><FlagEmoji emoji={first.flag} size={16} /> {countryName}{sep}</>;
    }
    return <><FlagEmoji emoji={first.flag} size={16} /> +{selectedCountries.length - 1}{sep}</>;
  };

  const getShortLabel = () => {
    if (selectedCountries.length === 0) return '🌍';
    const first = ALL_COUNTRIES.find(c => c.value === selectedCountries[0]);
    if (!first) return '🌍';
    const jurLabel = getJurisdictionLabel(first.value);
    const sep = jurLabel ? ` · ${jurLabel}` : '';

    if (selectedCountries.length === 1) {
      return <><FlagEmoji emoji={first.flag} size={16} /> {first.value}{sep}</>;
    }
    return <><FlagEmoji emoji={first.flag} size={16} /> +{selectedCountries.length - 1}</>;
  };

  // ─── Jurisdiction sub-tree ────────────────────────────────────────────

  const renderJurisdictionTree = (cc) => {
    const treeData = jurisdictionTrees[cc];
    if (!treeData?.has_sub_jurisdictions) return null;

    const allCodes = treeData.all_codes || [];
    const allMode = isAll(cc);

    return (
      <Box sx={{ pl: 1.5, pr: 0.5, pb: 0.5 }}>
        {/* "All jurisdictions" — acts as a reset button, not a checkbox */}
        <Box
          onClick={() => handleClickAll(cc)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            py: 0.4,
            px: 1,
            mb: 0.3,
            cursor: 'pointer',
            borderRadius: '6px',
            backgroundColor: allMode ? `${theme.palette.primary.main}18` : 'transparent',
            border: allMode ? `1px solid ${theme.palette.primary.main}40` : '1px solid transparent',
            transition: 'all 0.15s',
            '&:hover': {
              backgroundColor: allMode ? `${theme.palette.primary.main}22` : theme.palette.action.hover,
            },
          }}
        >
          <Typography sx={{
            fontSize: '0.74rem',
            fontWeight: allMode ? 700 : 500,
            color: allMode ? theme.palette.primary.main : theme.palette.text.secondary,
          }}>
            {t('jurisdiction_select_all')}
          </Typography>
        </Box>

        {/* Tree levels */}
        {treeData.tree.map((group) => {
          const levelKey = `${cc}_${group.level}`;
          const isExpanded = expandedLevels[levelKey] !== false;

          if (group.level === 'free_zone') {
            return (
              <Box key={levelKey}>
                <LevelHeader
                  label={t(LEVEL_LABEL_KEYS.free_zone)}
                  expanded={isExpanded}
                  onToggle={() => setExpandedLevels(p => ({ ...p, [levelKey]: !isExpanded }))}
                  theme={theme}
                />
                <Collapse in={isExpanded}>
                  {group.children.map((parentGroup) => {
                    const fzKey = `${cc}_fz_${parentGroup.parent_code}`;
                    const isFzExpanded = expandedFzParents[fzKey] !== false;
                    return (
                      <Box key={fzKey}>
                        <Box
                          onClick={() => setExpandedFzParents(p => ({ ...p, [fzKey]: !isFzExpanded }))}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 0.3,
                            py: 0.2, px: 1, pl: 2.5,
                            cursor: 'pointer', borderRadius: '4px',
                            '&:hover': { backgroundColor: theme.palette.action.hover },
                          }}
                        >
                          {isFzExpanded
                            ? <ExpandMoreIcon sx={{ fontSize: '0.75rem', color: theme.palette.text.disabled }} />
                            : <ChevronRightIcon sx={{ fontSize: '0.75rem', color: theme.palette.text.disabled }} />
                          }
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 500, color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                            {getName(parentGroup.parent_name)}
                          </Typography>
                        </Box>
                        <Collapse in={isFzExpanded}>
                          {parentGroup.zones.map((zone) => (
                            <JurisdictionItem
                              key={zone.code}
                              name={getName(zone.name)}
                              count={zone.doc_count}
                              checked={isChecked(cc, zone.code)}
                              onChange={() => handleClickJurisdiction(cc, zone.code, allCodes)}
                              disabled={zone.doc_count === 0}
                              indent={2}
                              theme={theme}
                            />
                          ))}
                        </Collapse>
                      </Box>
                    );
                  })}
                </Collapse>
              </Box>
            );
          }

          // Federal / Emirates
          return (
            <Box key={levelKey}>
              <LevelHeader
                label={t(LEVEL_LABEL_KEYS[group.level] || group.level)}
                expanded={isExpanded}
                onToggle={() => setExpandedLevels(p => ({ ...p, [levelKey]: !isExpanded }))}
                theme={theme}
              />
              <Collapse in={isExpanded}>
                {group.children.map((child) => (
                  <JurisdictionItem
                    key={child.code}
                    name={getName(child.name)}
                    count={child.doc_count}
                    checked={isChecked(cc, child.code)}
                    onChange={() => handleClickJurisdiction(cc, child.code, allCodes)}
                    disabled={child.doc_count === 0}
                    indent={1}
                    theme={theme}
                  />
                ))}
              </Collapse>
            </Box>
          );
        })}
      </Box>
    );
  };

  // ─── Popover content ──────────────────────────────────────────────────

  const isOpen = Boolean(anchorEl);

  const popoverContent = (
    <Box sx={{ minWidth: 280, maxWidth: 340, maxHeight: 420, overflowY: 'auto' }}>
      {ALL_COUNTRIES.map(opt => {
        const isSelected = selectedCountries.includes(opt.value);
        const hasSub = jurisdictionTrees[opt.value]?.has_sub_jurisdictions;
        return (
          <Box key={opt.value}>
            <MenuItem
              onClick={() => handleToggleCountry(opt.value)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1.5 }}
            >
              <Checkbox checked={isSelected} size="small" sx={{ p: 0.5 }} disableRipple />
              <Typography variant="body2" component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FlagEmoji emoji={opt.flag} /> {t(opt.labelKey, opt.value)}
              </Typography>
            </MenuItem>
            {isSelected && hasSub && renderJurisdictionTree(opt.value)}
          </Box>
        );
      })}
    </Box>
  );

  // ─── Render ───────────────────────────────────────────────────────────

  if (displayMode === 'icon') {
    return (
      <>
        <Box
          ref={iconButtonRef}
          onClick={() => !disabled && !isSaving && setAnchorEl(iconButtonRef.current)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: '6px',
            cursor: (disabled || isSaving) ? 'not-allowed' : 'pointer',
            backgroundColor: theme.palette.action.hover,
            transition: 'all 0.2s ease',
            opacity: (disabled || isSaving) ? 0.5 : 1,
            '&:hover': {
              backgroundColor: (disabled || isSaving) ? theme.palette.action.hover : theme.palette.action.selected,
            },
          }}
          title={selectedCountries.join(', ')}
        >
          {isSaving ? (
            <CircularProgress size={14} sx={{ color: theme.palette.primary.main }} />
          ) : (
            <PublicIcon sx={{ fontSize: 18, color: theme.palette.primary.main, flexShrink: 0 }} />
          )}
          <Typography
            variant="body2"
            component="span"
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              fontSize: '0.7rem', fontWeight: 600,
              maxWidth: '260px', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              color: theme.palette.text.primary, lineHeight: 1.4,
            }}
          >
            {getDisplayLabel()}
          </Typography>
        </Box>
        <Popover
          open={isOpen}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          {popoverContent}
        </Popover>
      </>
    );
  }

  return (
    <>
      <Box
        onClick={(e) => !disabled && !isSaving && setAnchorEl(e.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.5,
          py: 0.75,
          borderRadius: '8px',
          cursor: (disabled || isSaving) ? 'not-allowed' : 'pointer',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          transition: 'all 0.2s ease',
          opacity: (disabled || isSaving) ? 0.5 : 1,
          '&:hover': { borderColor: theme.palette.primary.main },
        }}
      >
        {isSaving ? (
          <CircularProgress size={14} sx={{ color: theme.palette.primary.main }} />
        ) : (
          <PublicIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
        )}
        <Typography
          variant="body2"
          component="span"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: '0.875rem', color: theme.palette.text.primary }}
        >
          {getShortLabel()}
        </Typography>
      </Box>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {popoverContent}
      </Popover>
    </>
  );
};

export default CompactCountryLanguageSelector;
